import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useRef, useState } from "react";
import { useUploadVariantsAPI, ValidVariant } from "@/app/hooks/api_hooks/coupon/useUploadVariantsAPI";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";

export interface IVariantUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (variants: ValidVariant[]) => void;
}

export default function VariantUploadModal({ open, onClose, onSuccess }: IVariantUploadModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { execute } = useUploadVariantsAPI();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      setError("Only CSV files are allowed.");
      setSelectedFile(null);
      e.target.value = "";
      return;
    }
    setError("");
    setSelectedFile(file);
  };

  const handleRemove = () => {
    setSelectedFile(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    handleRemove();
    onClose();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setLoading(true);
    const response = await execute(selectedFile);
    setLoading(false);
    if (response?.status) {
      onSuccess?.(response.data?.valid_variants ?? []);
      handleClose();
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-2xl transition-all sm:my-8 w-full max-w-[480px]">
                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                  <Dialog.Title className="text-base font-semibold text-gray-900">
                    Upload Variants
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-5 flex items-center justify-between rounded-xl bg-gray-50 p-4 border border-gray-100">
                  <div className="pr-3">
                    <span className="text-xs font-semibold text-gray-900 block">Sample Template</span>
                    <span className="text-[10.5px] text-gray-500 block mt-0.5">Use this CSV file format to prepare your variant data correctly.</span>
                  </div>
                  <a
                    href="/Variants_Sample.csv"
                    download="Variants_Sample.csv"
                    className="text-xs bg-black hover:bg-gray-800 text-white font-semibold px-3.5 py-2 rounded-lg transition-colors inline-flex items-center gap-1.5 shrink-0 shadow-sm"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </a>
                </div>

                {/* Upload File Section */}
                <div className="mb-6">
                  <p className="text-xs font-semibold text-gray-900 mb-2">
                    Select CSV File <span className="text-red-500">*</span>
                  </p>

                  {selectedFile ? (
                    <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 p-3.5">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-green-50 rounded-lg shrink-0">
                          <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <span className="text-xs font-medium text-gray-900 block truncate">{selectedFile.name}</span>
                          <span className="text-[10px] text-gray-400 block mt-0.5">
                            {(selectedFile.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemove}
                        className="p-1 rounded-full text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="csv-upload"
                      className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-black hover:bg-gray-50/50 transition-colors"
                    >
                      <div className="p-3 bg-gray-50 rounded-full mb-2">
                        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <span className="text-xs font-semibold text-gray-900">Click to upload CSV</span>
                      <span className="text-[10px] text-gray-400 mt-1">Only .csv files accepted</span>
                    </label>
                  )}

                  <input
                    ref={fileInputRef}
                    id="csv-upload"
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  {error && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-red-500">
                      <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      {error}
                    </div>
                  )}
                </div>
                <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-amber-50 p-3.5 border border-amber-200 text-amber-800">
                  <ExclamationTriangleIcon className="w-5 h-5 shrink-0 text-amber-500" aria-hidden="true" />
                  <p className="text-[11px] leading-relaxed font-semibold text-amber-800">
                    Selecting SKUs by uploading a file will override the existing <strong className="font-bold text-amber-800">Brand, Category, and Subcategory</strong> values.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-5">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-xs px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!selectedFile || loading}
                    onClick={handleUpload}
                    className="text-xs px-5 py-2.5 rounded-lg bg-black text-white font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    {loading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
